import {Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {UntypedFormGroup} from '@angular/forms';

import {DPAMRequestData, DPAMRequestMarketingDetailsI, GroupAttributeI} from '../../../services/product-setup.service';
import {ProductSetupChangesService} from '../../../../core/settings/product-setup-changes.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Observable, Subscription} from 'rxjs';
import {ValidationService} from '../../../services/validation.service';
import {formatHtml} from '../../prime-editor/custom-formats/source-code.module';

@UntilDestroy()
@Component({
  selector: 'app-group-marketing-details',
  templateUrl: './group-marketing-details.component.html',
  styleUrls: ['./group-marketing-details.component.scss']
})
export class GroupMarketingDetailsComponent implements OnInit, OnChanges, OnDestroy {
  formValues: DPAMRequestMarketingDetailsI;

  private afterSaveSubscription: Subscription;
  @Input() afterSave: Observable<void>;
  @Input() data = null as DPAMRequestData;
  @Input() marketingDetails: UntypedFormGroup;
  @Input() isShowWarning = false;
  @Input() disableSave = false;
  @Input() groupAttributes: GroupAttributeI[];
  @Input() isActionsDisabled = false;

  @Output() save: EventEmitter<{ editingRequest: DPAMRequestMarketingDetailsI, editingSection: string }> = new EventEmitter();
  @Output() cancel: EventEmitter<any> = new EventEmitter();
  @Output() addSpecificComment: EventEmitter<string> = new EventEmitter();
  @Output() switchCompare: EventEmitter<any> = new EventEmitter();
  @Output() currentForm: EventEmitter<UntypedFormGroup> = new EventEmitter();

  thisComponent: any;
  isEditMarketingDetails = false;
  hasMaxLengthError = false;

  constructor(private elementRef: ElementRef,
              private uiChangesService: ProductSetupChangesService,
              private validationService: ValidationService) {
  }

  ngOnInit() {
    this.thisComponent = this.elementRef.nativeElement;
    this.formValues = this.marketingDetails.value;
    this.uiChangesService.discardChanges()
      .pipe(untilDestroyed(this))
      .subscribe(() => this.discardChanges());
    this.marketingDetails.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(values => {
        this.checkForFormChanges(values);
        this.checkFormValid();
      });
    setTimeout(() => this.marketingDetails.patchValue({marketingDescriptionLong: this.marketingDetails.controls.marketingDescriptionLong.value}));
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes['data'] && changes['data'].currentValue) || (changes['marketingDetails'] && changes['marketingDetails'].currentValue)) {
      this.formValues = this.marketingDetails.value;
      this.isEditMarketingDetails = false;
    }
  }

  checkForFormChanges(data: any) {
    this.uiChangesService.hasChanges = this.uiChangesService.compareFormChanges(this.formValues, data);
  }

  @HostListener('document:formValuesSaved', ['$event'])
  onFormValuesSaved(ev: Event) {
    this.uiChangesService.hasChanges = false;
    this.formValues = this.marketingDetails.value;
  }

  discardChanges() {
    if (this.thisComponent.offsetParent != null) {
      this.uiChangesService.applyDiscardedChanges(this.marketingDetails, this.formValues);
      this.isEditMarketingDetails = false;
    }
  }

  ngOnDestroy() {
    this.afterSaveSubscription.unsubscribe();
  }

  onSave() {
    this.marketingDetails.value.marketingDescriptionLong = formatHtml(this.marketingDetails.value.marketingDescriptionLong);
    this.save.emit({
      editingRequest: Object.assign({
        marketingDetails: this.marketingDetails.value
      }),
      editingSection: 'group-marketing'
    });
    this.currentForm.emit(this.marketingDetails);
    this.isShowWarning = true;
    this.isEditMarketingDetails = false;
    this.formValues = this.marketingDetails.value;
    this.uiChangesService.hasChanges = false;
  }

  showError(controlName: string) {
    return !this.marketingDetails.get(controlName).valid && this.isShowWarning;
  }

  get isEditable() {
    return true;
  }

  onCancel() {
    if (this.isEditMarketingDetails) return;
    this.marketingDetails.patchValue({
      externalProductName: this.formValues.externalProductName,
      externalProductNameShort: this.formValues.externalProductNameShort,
      marketingDescriptionLong: this.formValues.marketingDescriptionLong,
      searchTerms: this.formValues.searchTerms
    });
  }

  onEditorEnterPressed($event: KeyboardEvent): any {
    //Prevent other events on enter key press
    $event.preventDefault();
    $event.stopPropagation();
  }

  showWarning(controlName: string) {
    return this.validationService.showWarning(this.marketingDetails, controlName, this.isShowWarning);
  }

  checkRequired(controlName: string) {
    return this.validationService.checkRequired(this.marketingDetails, controlName, this.isEditMarketingDetails, this.isShowWarning);
  }

  checkMaxLength(controlName: string) {
   return this.validationService.checkMaxLength(this.marketingDetails, controlName);
  }

  showMaxLengthErrorMessage(controlName: string) {
   return this.validationService.showMaxLengthErrorMessage(this.marketingDetails, controlName);
  }

  checkFormValid() {
    this.hasMaxLengthError = !this.validationService.isFormValid(this.marketingDetails);
  }
}
